import { Button } from '@cimplico/components';
import { useFormContext, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toastNotification } from '../../../../../../../components/toast/Toast';
import { useFirmContext } from '../../../../../../../providers/FirmProvider';
import { CreateFYIGroupsResponse, useCreateFYIGroups } from '../../../../../../../queries/use-create-fyi-groups';
import { chunk } from '../../../../../../../utilities/chunk';
import { OnboardingFormData } from './FYIGroupsOnboarding';
import { formatGroupsChunk, formatGroupRowsError, formatSuccessMessages } from './utils';
import queryClient from '../../../../../../../query-client';
import axios, { AxiosError } from 'axios';

type SubmitButtonProps = {
    onSubmit: (isSubmitting: boolean) => void;
    onProgressChange: (chunkLength: number) => void;
    onTotalChange: (total: number) => void;
    onClearSearch: () => void;
    onSetAlert: (alert?: { errors: { [groupName: string]: string[] }; success: CreateFYIGroupsResponse }) => void;
};

function SubmitButton({ onSubmit, onSetAlert, onProgressChange, onTotalChange, onClearSearch }: SubmitButtonProps): JSX.Element {
    const { control, setValue, reset } = useFormContext<OnboardingFormData>();
    const { shortId } = useFirmContext();
    const { mutateAsync, isLoading } = useCreateFYIGroups();

    const data = useWatch({ control, name: 'data' });
    const navigate = useNavigate();

    const canSubmit = data.filter((d) => d.group.isSelected).length > 0;

    function resetOnboarding(): void {
        onProgressChange(0);
        onTotalChange(0);
        onSubmit(false);
        onClearSearch();
    }

    async function handleSubmit(): Promise<void> {
        onSubmit(true);
        onSetAlert(undefined);

        const selectedGroups = data.filter((d) => d.group.isSelected);
        onTotalChange(selectedGroups.length);

        const { groupsWithoutDayAndMonthValues, groupsWithoutDayValues, invalidEntitiesWithinGroup } = formatGroupRowsError(data);
        if (
            groupsWithoutDayAndMonthValues.length > 0 ||
            groupsWithoutDayValues.length > 0 ||
            Object.keys(invalidEntitiesWithinGroup).length > 0
        ) {
            setValue(
                `data`,
                data.map((d, idx) => {
                    if (groupsWithoutDayAndMonthValues.includes(idx)) {
                        if (d.group.fyiGroup.existingMapping) {
                            return d;
                        }
                        return { ...d, group: { ...d.group, monthError: 'Enter a month value', dayError: 'Enter a day value' } };
                    }
                    if (groupsWithoutDayValues.includes(idx)) {
                        if (d.group.fyiGroup.existingMapping) {
                            return d;
                        }
                        return { ...d, group: { ...d.group, dayError: 'Enter a day value' } };
                    }
                    if (Object.keys(invalidEntitiesWithinGroup).includes(idx.toString())) {
                        const match = invalidEntitiesWithinGroup[idx];
                        return {
                            entities: d.entities.map((ent, i) => {
                                if (match.entitiesWithoutDayAndMonthValues.includes(i)) {
                                    return { ...ent, monthError: 'Enter a month value', dayError: 'Enter a day value' };
                                }
                                if (match.entitiesWithoutDayValues.includes(i)) {
                                    return { ...ent, dayError: 'Enter a day value' };
                                }
                                return ent;
                            }),
                            group: {
                                ...d.group,
                                dayError:
                                    match.groupWithoutDayValue || match.groupWithoutDayAndMonthValue ? 'Enter a day value' : undefined,
                                monthError: match.groupWithoutDayAndMonthValue ? 'Enter a month value' : undefined,
                            },
                        };
                    }
                    return d;
                })
            );

            resetOnboarding();
            return;
        }

        const chunks = chunk(selectedGroups);
        const errorList: { [entityName: string]: string[] } = {};

        let entitiesOnboarded = 0;
        let existingEntitiesMapped = 0;
        let existingGroupsMapped = 0;
        let groupsOnboarded = 0;

        for (const chunk of chunks) {
            try {
                const res = await mutateAsync(formatGroupsChunk(chunk));
                entitiesOnboarded += res.entitiesOnboarded;
                existingEntitiesMapped += res.existingEntitiesMapped;
                existingGroupsMapped += res.existingGroupsMapped;
                groupsOnboarded += res.groupsOnboarded;
            } catch (res) {
                if (axios.isAxiosError(res)) {
                    entitiesOnboarded += res.response?.data.entitiesOnboarded;
                    existingEntitiesMapped += res.response?.data.existingEntitiesMapped;
                    existingGroupsMapped += res.response?.data.existingGroupsMapped;
                    groupsOnboarded += res.response?.data.groupsOnboarded;

                    const errorResult = res.response?.data.errors;

                    if (!errorResult) {
                        errorList.Error = ['An unknown error occurred!'];
                    } else {
                        for (const group of Object.keys(errorResult)) {
                            errorList[errorResult[group].name] = errorResult[group].reasons;
                        }
                    }
                }
            }

            onProgressChange(chunk.length);
        }

        // If all groups and entities onboarding succeeded, show toast and redirect
        if (Object.keys(errorList).length === 0) {
            const successMessages = formatSuccessMessages({
                entitiesOnboarded,
                existingEntitiesMapped,
                groupsOnboarded,
                existingGroupsMapped,
            });

            if (successMessages.length > 0) {
                toastNotification(
                    'success',
                    `Successfully onboarded FYI groups${entitiesOnboarded || existingEntitiesMapped ? ' and entities' : ''}!`,
                    successMessages.join(' ')
                );
            }

            navigate(`/${shortId}/groups`);
            resetOnboarding();
            queryClient.invalidateQueries(['fyi', 'groups']);

            return;
        }

        // Else, show toast for successful onboarding, show banner for errors
        onSetAlert &&
            onSetAlert({
                errors: errorList,
                success: { entitiesOnboarded, existingEntitiesMapped, groupsOnboarded, existingGroupsMapped },
            });

        queryClient.invalidateQueries(['fyi', 'groups']);
        resetOnboarding();
        reset();
    }

    return (
        <div className="mt-4 flex justify-end">
            <Button
                label={isLoading ? 'Creating...' : 'Create Groups'}
                disabled={!canSubmit || isLoading}
                isLoading={isLoading}
                onClick={handleSubmit}
            />
        </div>
    );
}

export default SubmitButton;
